// src/components/ProfileOnboardingGate.tsx
import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import ProfileOnboardingModal from "../onboarding/ProfileOnboardingModal";

type ProfileOnboardingGateProps = {
  children: ReactNode;
  disabled?: boolean;
};

function ProfileOnboardingGate({
  children,
  disabled = false,
}: ProfileOnboardingGateProps) {
  const location = useLocation();
  const [show, setShow] = useState(false);

  const mounted = useRef(true);
  const claimingRef = useRef(false);
  const checkingRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get("redirect") || "";

  const isClubFlow =
    disabled ||
    location.pathname.startsWith("/invite/") ||
    location.pathname.startsWith("/club/") ||
    location.pathname.startsWith("/auth/callback") ||
    redirectParam.startsWith("/invite/");

  const claimInvitesIfAny = async (email?: string | null, uid?: string | null) => {
    if (!email || !uid || claimingRef.current) return;

    claimingRef.current = true;
    try {
      const { error: uErr } = await supabase
        .from("team_members")
        .update({
          user_id: uid,
          accepted_at: new Date().toISOString(),
          status: "active",
        })
        .eq("invited_email", email)
        .is("user_id", null)
        .in("status", ["invited", "active"]);

      if (uErr) {
        console.warn("[ProfileOnboardingGate] claimInvites error:", uErr);
      }
    } catch (e) {
      console.warn("[ProfileOnboardingGate] claimInvites exception:", e);
    } finally {
      claimingRef.current = false;
    }
  };

  const checkFromSession = async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;

    try {
      if (isClubFlow) {
        if (mounted.current) setShow(false);
        return;
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.warn("[ProfileOnboardingGate] getSession error:", error);
      }

      const session = data?.session ?? null;
      const user = session?.user ?? null;

      if (!user) {
        if (mounted.current) setShow(false);
        lastUserIdRef.current = null;
        return;
      }

      if (lastUserIdRef.current !== user.id) {
        lastUserIdRef.current = user.id;
      }

      await claimInvitesIfAny(user.email, user.id);

      const { data: profile, error: pErr } = await supabase
        .from("user_profiles")
        .select("id, full_name")
        .eq("id", user.id)
        .maybeSingle();

      if (pErr) {
        console.error("[ProfileOnboardingGate] profiles query error:", pErr);
        if (mounted.current) setShow(false);
        return;
      }

      if (mounted.current) setShow(!profile);
    } finally {
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    mounted.current = true;
    checkFromSession();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "SIGNED_OUT") {
        lastUserIdRef.current = null;
        if (mounted.current) setShow(false);
        return;
      }

      await checkFromSession();
    });

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkFromSession();
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mounted.current = false;
      sub?.subscription?.unsubscribe?.();
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClubFlow]);

  return (
    <>
      {children}
      {!isClubFlow && show && <ProfileOnboardingModal onDone={() => setShow(false)} />}
    </>
  );
}

export default ProfileOnboardingGate;